import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import AddVehicle from "./Add-Vehicle";
import vehicleService from "../../services/vehicles/vehicleservice";
import { InputSwitch } from "primereact/inputswitch";

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchKey, setSearchKey] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [sortField, setSortField] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null | undefined>(-1);

  const [addVehicleDialog, setAddVehicleDialog] = useState(false);
  const [editVehicleId, setEditVehicleId] = useState<number | null>(null);

  const toast = useRef<Toast>(null);

  const showToast = (message: string, severity: "success" | "error") => {
    toast.current?.show({
      severity,
      summary: severity === "success" ? "Success" : "Error",
      detail: message,
      life: 3000,
    });
  };

  const loadVehicles = async (overrideSearchKey?: string) => {
    const request = {
      searchKey: overrideSearchKey !== undefined ? overrideSearchKey : searchKey,
      sortBy: sortField,
      sortDesc: sortOrder === -1,
      page,
      pageSize,
    };

    const result = await vehicleService.getVehicles(request);

    setVehicles(result.data || []);
    setTotalRecords(result.totalRecords || 0);
  };

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [page, pageSize, sortField, sortOrder]);

  const handleFilter = () => {
    setPage(1);
    setPageSize(10);
    loadVehicles();
  };

  const handleReset = () => {
    setSearchKey("");
    setPage(1);
    setPageSize(10);
    setSortField("createdDate");
    setSortOrder(-1);
    loadVehicles("");
  };

  const formatDate = (value: any) => {
    if (!value) return "";

    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const toggleStatus = async (id: number, newStatus: boolean) => {
    const res = await vehicleService.updateVehicleStatus(id, newStatus);

    if (res.isSuccess) {
      showToast(res.message, "success");
      loadVehicles();
    } else {
      showToast(res.message, "error");
    }
  };

  return (
    <div className="p-3">

      {/* GLOBAL TOAST */}
      <Toast ref={toast} />

      <h2 className="text-xl font-semibold mb-3">View Vehicles</h2>

      {/* Search Section */}
      <div className="flex align-items-center justify-content-between mb-3" style={{ gap: "10px" }}>
        <div className="flex gap-3">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder="Keyword Search"
              style={{ width: "250px" }}
            />
          </span>

          <Button label="Filter" className="p-button-success" onClick={handleFilter} />
          <Button label="Reset" className="p-button-secondary" onClick={handleReset} />
        </div>

        <Button
          label="Add Vehicle"
          icon="pi pi-plus"
          onClick={() => {
            setEditVehicleId(null);
            setAddVehicleDialog(true);
          }}
        />
      </div>

      {/* Vehicle Table */}
      <DataTable
        value={vehicles}
        paginator
        lazy
        rows={pageSize}
        totalRecords={totalRecords}
        first={(page - 1) * pageSize}
        stripedRows
        showGridlines
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        onPage={(e: any) => {
          setPage(e.page + 1);
          setPageSize(e.rows);
        }}
        onSort={(e: any) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        className="shadow-2"
      >
        <Column field="vehicleNumber" header="Vehicle Number" sortable />
        <Column field="vehicleTypeName" header="Vehicle Type" sortable />
        <Column field="vehicleOwnershipType" header="Type" sortable />
        <Column field="ownerName" header="Owner" sortable />
        <Column field="posCode" header="POS" sortable />      
        <Column field="insuranceExpiryDate" header="Insurance Expiry" body={(row) => formatDate(row.insuranceExpiryDate)} sortable />
        <Column field="permitExpiryDate" header="Permit Expiry" body={(row) => formatDate(row.permitExpiryDate)} sortable />
        <Column field="fcExpiryDate" header="FC EXpiry" body={(row) => formatDate(row.fcExpiryDate)} sortable />
        <Column field="emissionExpiryDate" header="Emission Expiry" body={(row) => formatDate(row.emissionExpiryDate)} sortable />
        <Column field="createdByName" header="Created BY" sortable />
        <Column field="createdDate" header="Created Date" body={(row) => formatDate(row.createdDate)} sortable />

        <Column
          header="Status"
          body={(row) => (
            <div className="flex align-items-center gap-2">
              {/* Input Switch */}
              <InputSwitch
                checked={row.status}
                onChange={(e: any) => toggleStatus(row.id, e.value)}
              />

              {/* Label Text */}
              <span
                className={
                  row.status
                    ? "text-green-600 font-semibold"
                    : "text-red-600 font-semibold"
                }
              >
                {row.status ? "Active" : "Inactive"}
              </span>
            </div>
          )}
        />

        {/* Edit Action */}
        <Column
          header="Action"
          body={(row) => (
            <div className="flex gap-3">
              <i
                className="pi pi-pencil cursor-pointer text-blue-600"
                onClick={() => {
                  setEditVehicleId(row.id);
                  setAddVehicleDialog(true);
                }}
              ></i>
            </div>
          )}
        />
      </DataTable>

      {/* Add / Edit Vehicle Dialog */}
      <Dialog
        header={editVehicleId ? "Edit Vehicle" : "Add Vehicle"}
        visible={addVehicleDialog}
        style={{ width: "55vw" }}
        modal
        onHide={() => {
          setAddVehicleDialog(false);
          setEditVehicleId(null);
        }}
      >
        <AddVehicle
          vehicleId={editVehicleId}
          showToast={showToast}
          onSaved={() => {
            loadVehicles();
            setAddVehicleDialog(false);
            setEditVehicleId(null);
          }}
        />
      </Dialog>
    </div>
  );
};

export default VehicleList;
